import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Paper, Rating, Button, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ConstructionIcon from '@mui/icons-material/Construction';
import HistoryIcon from '@mui/icons-material/History';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ownerImage from '../images/raczjeno.jpg';
import '../styles/About.scss';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const About = () => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [openMilestones, setOpenMilestones] = useState({});

    const toggleMilestone = (index) => {
        setOpenMilestones(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const companyHistory = [
        {
            year: '2012',
            title: 'A kezdetek',
            description: 'A Toronyszerver KFT megalapítása Gyömrőn. Cégünk közületi intézmények és ipari épületek generálkivitelezésével kezdett foglalkozni, már akkor is a minőségi munkavégzés és az ügyfélelégedettség volt a fő célunk.'
        },
        {
            year: '2014',
            title: 'Szakmai fejlődés',
            description: 'Szakemberhálózatunk kiépítése és bővítése. Olyan csapatot építettünk, akik magas szakmai felkészültségük mellett elkötelezettek a minőségi munkavégzés mellett.'
        },
        {
            year: '2016',
            title: 'Technológiai fejlesztés',
            description: 'Korszerű építőanyagok és modern technológiák bevezetése. Kiemelt figyelmet fordítunk az alkalmazandó technikák körültekintő kiválasztására.'
        },
        {
            year: '2018',
            title: 'Piaci áttörés',
            description: 'Jelentős lakó- és ipari projektek megvalósítása, stabil partneri kapcsolatok kiépítése. Megbízható, korrekt partnerként együttműködünk megrendelőinkkel.'
        },
        {
            year: '2020',
            title: 'Innovatív megoldások',
            description: 'Környezettudatos építészeti megoldások bevezetése. Szakembereink folyamatos képzése és a legújabb technológiák alkalmazása a még magasabb minőség érdekében.'
        },
        {
            year: '2023',
            title: 'Vezető pozíció',
            description: 'Büszkék vagyunk arra, hogy munkáink minőségében visszatükröződnek az általunk vallott elvek és tapasztalatok.'
        },
        {
            year: '2025',
            title: 'A Jövőbe mutató megoldások',
            description: 'Minden olyan megújuló energiaforrás és fenntartható technológia alkalmazása, amely a zöld energia előállítását és felhasználását támogatja.'
        }
    ];

    const companyValues = [
        /*{
            icon: <HandshakeIcon />,
            title: 'Megbízhatóság',
            description: 'Határidők pontos betartása és transzparens kommunikáció minden projektben.'
        },
        {
            icon: <EngineeringIcon />,
            title: 'Szakértelem',
            description: 'Több évtizedes tapasztalat és folyamatosan képzett szakembergárda.'
        },
        {
            icon: <ConstructionIcon />,
            title: 'Minőség',
            description: 'Prémium alapanyagok és korszerű technológiák alkalmazása.'
        }*/
    ];

    const reviews = [
        {
            name: "Nagy Péter",
            position: "Műszaki Igazgató",
            company: "Tech Solutions Kft.",
            rating: 5,
            text: "A Toronyszerver KFT-vel való együttműködés során megtapasztaltuk, hogy mit jelent a valódi szakértelem és profizmus. Az ipari létesítményünk kivitelezése során minden elvárásunkat túlteljesítették.",
            date: "2023. október"
        },
        {
            name: "Kovács István",
            position: "Projektvezető",
            company: "Építő-Ker Zrt.",
            rating: 5,
            text: "Kiemelkedő szakmai tudás, rugalmas hozzáállás és kifogástalan minőség jellemzi a munkájukat. A projekt során felmerülő kihívásokat proaktívan és hatékonyan kezelték.",
            date: "2023. szeptember"
        }
    ];

    return (
        <>
            <Helmet>
                <title>Rólunk | Toronyszerver KFT - Vezető Építőipari Kivitelező Gyömrőn</title>
                <meta name="description" content="A Toronyszerver KFT Gyömrő megbízható építőipari vállalkozása. 2012 óta foglalkozunk lakó- és ipari épületek generálkivitelezésével. Minőségi kivitelezés, megbízható szakértelem." />
                <meta name="keywords" content="építőipari kivitelezés, generálkivitelezés, lakóépületek, ipari épületek, Gyömrő, építőipar, megbízható kivitelező" />
                <meta property="og:title" content="Toronyszerver KFT - Az építőipar megbízható partnere" />
                <meta property="og:description" content="Ismerje meg a Toronyszerver KFT történetét és értékeit. 2012 óta nyújtunk kiváló minőségű és megbízható szolgáltatást az építőiparban." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://toronyszerver.hu/rolunk" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://toronyszerver.hu/rolunk" />
            </Helmet>

            <div className="about-page">
                {/* Hero Section */}
                <section className="hero-section">
                    <Container maxWidth="lg">
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <Typography variant="h1" className="hero-title">
                                        Megbízható <span className="highlight">Partner</span>
                                    </Typography>
                                    <Typography variant="h2" className="hero-subtitle">
                                        Az építőipar szakértője 2012 óta
                                    </Typography>
                                    <Typography className="hero-description">
                                        A Toronyszerver KFT az építőipar egy megbízható és tapasztalt szereplője. Célunk az, hogy ügyfeleinknek kiváló minőségű és megbízható szolgáltatást nyújtsunk, minden projektet magas színvonalon és hatékonyan teljesítve.
                                    </Typography>
                                </motion.div>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                    className="hero-image-container"
                                >
                                    <div className={`hero-image-placeholder ${imageLoaded ? 'loaded' : ''}`}>
                                        <motion.div
                                            animate={{
                                                scale: [1, 1.1, 1],
                                                opacity: [0.6, 0.8, 0.6]
                                            }}
                                            transition={{
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        >
                                            <img
                                                src="/images/logo/logo2.png"
                                                alt="Loading..."
                                                style={{ width: '60px', height: '60px', opacity: 0.5 }}
                                            />
                                        </motion.div>
                                    </div>
                                    <img
                                        src={ownerImage}
                                        alt="Rácz Jenő - A Toronyszerver KFT ügyvezetője"
                                        className={`hero-image ${imageLoaded ? 'loaded' : ''}`}
                                        loading="eager"
                                        onLoad={() => setImageLoaded(true)}
                                    />
                                </motion.div>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Mission Section */}
                <section className="mission-section">
                    <Container maxWidth="lg">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <Typography variant="h2" className="section-title" align="center">
                                Küldetésünk
                            </Typography>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Paper className="mission-card">
                                        <Box className="mission-content">
                                            <Box className="mission-header">
                                                <Typography variant="h3" className="mission-title">
                                                    Szakszerű kivitelezés, megbízható minőség
                                                </Typography>
                                                <Typography className="mission-subtitle">
                                                    Megrendelőink által biztosított tervdokumentációk alapján az építkezési munkafolyamatok koordinálásával, szervezésével együtt valósítjuk meg a családi házak, társasházak és középületek szakszerű kivitelezését.
                                                </Typography>
                                            </Box>
                                            <Grid container spacing={4} className="mission-points">
                                                <Grid item xs={12} md={4}>
                                                    <Box className="mission-point">
                                                        <Box className="point-icon">
                                                            <EngineeringIcon />
                                                        </Box>
                                                        <Typography variant="h4">
                                                            Szakszerű kivitelezés
                                                        </Typography>
                                                        <Typography>
                                                            Professzionális projektmenedzsment és kivitelezés minden építkezésen
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Box className="mission-point">
                                                        <Box className="point-icon">
                                                            <HandshakeIcon />
                                                        </Box>
                                                        <Typography variant="h4">
                                                            Megbízható partner
                                                        </Typography>
                                                        <Typography>
                                                            Transzparens kommunikáció és korrekt együttműködés
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Box className="mission-point">
                                                        <Box className="point-icon">
                                                            <ConstructionIcon />
                                                        </Box>
                                                        <Typography variant="h4">
                                                            Minőségi kivitelezés
                                                        </Typography>
                                                        <Typography>
                                                            Korszerű technológiák és prémium alapanyagok
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </motion.div>
                    </Container>
                </section>

                {/* History Section */}
                <section className="history-section">
                    <Container maxWidth="lg">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <Typography variant="h2" className="section-title" align="center">
                                Történetünk
                            </Typography>
                            <div className="timeline">
                                {companyHistory.map((milestone, index) => (
                                    <motion.div
                                        key={index}
                                        className={`timeline-item ${openMilestones[index] ? 'open' : ''}`}
                                        initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                    >
                                        <Paper 
                                            className="timeline-content"
                                            onClick={() => toggleMilestone(index)}
                                        >
                                            <div className="timeline-header">
                                                <Typography variant="h3" className="year">
                                                    {milestone.year}
                                                </Typography>
                                                <IconButton 
                                                    className="toggle-button"
                                                    size="small"
                                                >
                                                    {openMilestones[index] ? <RemoveIcon /> : <AddIcon />}
                                                </IconButton>
                                            </div>
                                            <motion.div
                                                className="timeline-details"
                                                initial={false}
                                                animate={{
                                                    height: openMilestones[index] ? 'auto' : 0,
                                                    opacity: openMilestones[index] ? 1 : 0
                                                }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: "easeInOut"
                                                }}
                                            >
                                                <Typography variant="h4" className="title">
                                                    {milestone.title}
                                                </Typography>
                                                <Typography className="description">
                                                    {milestone.description}
                                                </Typography>
                                            </motion.div>
                                        </Paper>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </Container>
                </section>

                {/* Leadership Section */}
                <section className="leadership-section">
                    <Container maxWidth="lg">
                        <Grid container spacing={6} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <motion.div
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <Typography variant="h2" className="section-title">
                                        Vezetői Szemlélet
                                    </Typography>
                                    <Typography variant="h3" className="leader-name">
                                        Rácz Jenő
                                    </Typography>
                                    <Typography variant="h4" className="leader-position">
                                        Alapító, Ügyvezető
                                    </Typography>
                                    <Typography className="leader-description">
                                        "Az építőiparban minden projekt egyedi kihívásokat rejt. Sikerünk kulcsa, hogy minden megbízást ugyanazzal a szakmai alázattal és precizitással közelítünk meg. Célunk nem csupán épületek létrehozása, hanem olyan terek kialakítása, amelyek valódi értéket teremtenek ügyfeleink számára."
                                    </Typography>
                                    <Box className="leader-credentials">
                                        <Typography variant="h6">Szakmai Háttér:</Typography>
                                        <ul>
                                            <li>Építőmérnöki diploma és szakmérnöki végzettség</li>
                                            <li>20+ év vezetői tapasztalat az építőiparban</li>
                                            <li>Projektmenedzsment szakértő</li>
                                        </ul>
                                    </Box>
                                </motion.div>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <motion.div
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <Grid container spacing={3}>
                                        {companyValues.map((value, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Paper className="value-card">
                                                    <Box className="icon-wrapper">{value.icon}</Box>
                                                    <Box className="content">
                                                        <Typography variant="h5">{value.title}</Typography>
                                                        <Typography>{value.description}</Typography>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </motion.div>
                            </Grid>
                        </Grid>
                    </Container>
                </section>

                {/* Reviews Section */}
                <section className="reviews-section">
                    <Container maxWidth="lg">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <Typography variant="h2" className="section-title" align="center">
                                Ügyfeleink Visszajelzései
                            </Typography>
                            <Grid container spacing={4}>
                                {reviews.map((review, index) => (
                                    <Grid item xs={12} md={6} key={index}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 30 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.5, delay: index * 0.1 }}
                                        >
                                            <Paper className="review-card">
                                                <Box className="review-header">
                                                    <Box className="review-rating">
                                                        <Rating value={review.rating} readOnly size="large" />
                                                    </Box>
                                                    <FormatQuoteIcon className="quote-icon" />
                                                </Box>
                                                <Typography className="review-text">
                                                    {review.text}
                                                </Typography>
                                                <Box className="review-footer">
                                                    <Box className="review-author">
                                                        <Box className="author-avatar">
                                                            {review.name.charAt(0)}
                                                        </Box>
                                                        <Box className="author-info">
                                                            <Typography className="author-name">
                                                                {review.name}
                                                            </Typography>
                                                            <Typography className="author-position">
                                                                {review.position}
                                                            </Typography>
                                                            <Typography className="author-company">
                                                                {review.company}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Typography className="review-date">
                                                        {review.date}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Container>
                </section>

                {/* CTA Section */}
                <section className="cta-section">
                    <Container maxWidth="lg">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="cta-content"
                        >
                            <Typography variant="h2">
                                Megbízható építési partnerre van szüksége?
                            </Typography>
                            <Typography>
                                Forduljon hozzánk bizalommal! Vegye fel velünk a kapcsolatot és beszéljük meg, hogyan segíthetünk megvalósítani elképzeléseit.
                            </Typography>
                            <Button
                                component={Link}
                                to="/kapcsolat"
                                variant="contained"
                                color="primary"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                                className="cta-button"
                            >
                                Kapcsolatfelvétel
                            </Button>
                        </motion.div>
                    </Container>
                </section>
            </div>
        </>
    );
};

export default About; 